import React from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  Inventory2Outlined,
  UnfoldMore,
} from "@mui/icons-material";
import { getPodOrderRowKey, PodOrder } from "../../../models/PodOrder/PodOrder";
import { PodOrderPrintingTypeBadge, PodOrderProgressStageBadge } from "./podOrderBadges";
import { formatDateWithoutTime } from "../common/functions/formatDate";
import { observer } from "mobx-react-lite";
export interface PodOrderListTableProps {
  loading: boolean;
  orders: PodOrder[];
  selectionEnabled?: boolean;
  isRowSelected: (order: PodOrder) => boolean;
  sortBy?: string;
  sortType?: "ASC" | "DESC";
  page: number;
  limit: number;
  totalCount: number;
  onSortChange: (column: string) => void;
  areAllSelected: () => boolean;
  areSomeSelected: () => boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (order: PodOrder) => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const PodOrderListTable: React.FC<PodOrderListTableProps> = ({
  loading,
  orders,
  selectionEnabled = true,
  isRowSelected,
  sortBy,
  sortType,
  page,
  limit,
  totalCount,
  onSortChange,
  areAllSelected,
  areSomeSelected,
  onToggleSelectAll,
  onToggleRow,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();

  const thSx = {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: theme.palette.customColors?.grayText,
    letterSpacing: "0.05em",
    whiteSpace: "nowrap" as const,
    py: 1.5,
    px: 2,
    borderBottom: `1px solid ${theme.palette.customColors?.lightGray}`,
    backgroundColor: theme.palette.customColors?.lightGrayBackground,
  };

  const tdSx = {
    fontSize: "0.82rem",
    fontWeight: 400,
    color: theme.palette.text.primary,
    py: 1.25,
    px: 2,
    borderBottom: `1px solid ${theme.palette.customColors?.lightGreyColor}`,
  };

  const centerCellSx = {
    th: { ...thSx, textAlign: "center" as const },
    td: { ...tdSx, textAlign: "center" as const },
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "40vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        mt: 1.5,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: theme.palette.customColors?.lightGray,
        overflow: "hidden",
        boxShadow: `0 1px 3px ${theme.palette.customColors?.shadowVeryLight}`,
      }}
    >
      <TableContainer sx={{ maxHeight: window.innerHeight - 360 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {selectionEnabled && (
                <TableCell padding="checkbox" sx={{ ...thSx, width: 48, px: 1.5 }}>
                  <Checkbox
                    color="primary"
                    size="small"
                    indeterminate={areSomeSelected()}
                    checked={areAllSelected()}
                    disabled={orders.length === 0}
                    onClick={onToggleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell sx={thSx}>
                <Box
                  onClick={() => onSortChange("order_id")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { color: theme.palette.customColors?.blackText },
                  }}
                >
                  ORDER ID
                  {sortBy === "order_id" ? (
                    sortType === "ASC" ? (
                      <ArrowUpward sx={{ fontSize: 13 }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 13 }} />
                    )
                  ) : (
                    <UnfoldMore sx={{ fontSize: 13, opacity: 0.4 }} />
                  )}
                </Box>
              </TableCell>

              <TableCell align="center" sx={centerCellSx.th}>
                SIR ID
              </TableCell>



              <TableCell sx={thSx}>PRODUCT</TableCell>

              {["EXPECTED DISPATCH DATE", "PRINTING TYPE", "ORDER QTY", "OUTWARD QTY", "INWARD QTY", "REJECTED QTY"].map(col => (
                <TableCell key={col} align="center" sx={centerCellSx.th}>
                  {col}
                </TableCell>
              ))}

              <TableCell align="center" sx={centerCellSx.th}>
                <Box
                  onClick={() => onSortChange("raw_inward_date")}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { color: theme.palette.customColors?.blackText },
                  }}
                >
                  ORDER DATE
                  {sortBy === "raw_inward_date" ? (
                    sortType === "ASC" ? (
                      <ArrowUpward sx={{ fontSize: 13 }} />
                    ) : (
                      <ArrowDownward sx={{ fontSize: 13 }} />
                    )
                  ) : (
                    <UnfoldMore sx={{ fontSize: 13, opacity: 0.4 }} />
                  )}
                </Box>
              </TableCell>
              <TableCell align="center" sx={centerCellSx.th}>
                LATEST SHIPMENT ID
              </TableCell>
              <TableCell align="center" sx={centerCellSx.th}>
                STAGE
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectionEnabled ? 10 : 9} align="center" sx={{ py: 8, color: theme.palette.customColors?.mediumGray, fontSize: "0.875rem", border: 0 }}>
                  No POD Orders match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              orders.map(order => {
                const isSelected = selectionEnabled && isRowSelected(order);
                const orderDate = order.raw_inward_date ?? order.print_outward_date;
                return (
                  <TableRow
                    key={getPodOrderRowKey(order)}
                    sx={{
                      backgroundColor: isSelected ? theme.palette.customColors?.blueLightBackground : theme.palette.customColors?.white,
                      "&:hover": {
                        backgroundColor: isSelected
                          ? theme.palette.customColors?.blueLightBackground
                          : theme.palette.customColors?.lightGrayBackground,
                      },
                      "& td": { borderBottom: `1px solid ${theme.palette.customColors?.lightGreyColor}` },
                    }}
                  >
                    {selectionEnabled && (
                      <TableCell
                        padding="checkbox"
                        sx={{ px: 1.5, borderBottom: `1px solid ${theme.palette.customColors?.lightGreyColor}` }}
                      >
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={isSelected}
                          onClick={() => onToggleRow(order)}
                        />
                      </TableCell>
                    )}

                    <TableCell sx={tdSx}>
                      <Box
                        component="span"
                        sx={{
                          color: theme.palette.customColors?.purpleMain,
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        #{order.order_id}
                      </Box>
                    </TableCell>

                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.stock_issue_request_id ?? "—"}
                    </TableCell>

                    <TableCell sx={{ ...tdSx, minWidth: 180 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors?.lightGray,
                            backgroundColor: theme.palette.customColors?.lightGreyColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Inventory2Outlined sx={{ fontSize: 18, color: theme.palette.customColors?.mediumGray }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {order.product_name?.trim() || "—"}
                          </Box>
                          <Box sx={{ fontSize: "0.75rem", color: theme.palette.customColors.grayText, mt: 0.25 }}>
                            Variant: {order.sku || "—"}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={centerCellSx.td}>
                      {order.dispatch_date
                        ? formatDateWithoutTime(order.dispatch_date, {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })
                        : "—"}
                    </TableCell>
                    <TableCell align="center" sx={centerCellSx.td}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <PodOrderPrintingTypeBadge name={order.printing_type} />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.ordered_qty}
                    </TableCell>
                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.total_quantity_sent || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.total_print_inwarded_qty || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.total_rejected_quantity || "-"}
                    </TableCell>
                    <TableCell align="center" sx={centerCellSx.td}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                          color: theme.palette.customColors?.grayText,
                        }}
                      >

                        <Box component="span" sx={{ fontSize: "0.8rem" }}>
                          {orderDate
                            ? formatDateWithoutTime(orderDate, {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                            : "—"}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...centerCellSx.td, fontWeight: 600 }}>
                      {order.shipment_id ?? "—"}
                    </TableCell>

                    <TableCell align="center" sx={centerCellSx.td}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <PodOrderProgressStageBadge status={order.status} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 30, 50, 100, 200]}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            borderTop: `1px solid ${theme.palette.customColors?.lightGray}`,
            backgroundColor: theme.palette.customColors?.white,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "0.8rem",
              color: theme.palette.customColors?.grayText,
            },
          }}
        />
      )}
    </Paper>
  );
};

export default observer(PodOrderListTable);
